import {
  Component,
  EventEmitter,
  Output,
  input,
  signal,
  OnDestroy,
  inject,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { PdfViewer } from '../pdf-viewer';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'lib-app-file-uploader',
  imports: [ButtonModule, PdfViewer, TranslocoPipe],
  templateUrl: './file-uploader.html',
  styleUrl: './file-uploader.css',
})
export class FileUploader implements OnDestroy {
  maxFileSizeMB = input<number>(10);
  acceptedFileTypes = input<string[]>([]);
  showPreview = input<boolean>(true);
  multiple = input<boolean>(false);
  disabled = input<boolean>(false);

  @Output() fileChange = new EventEmitter<File | null>();
  @Output() fileSelect = new EventEmitter<File>();
  @Output() fileRemove = new EventEmitter<void>();
  @Output() dragStateChange = new EventEmitter<boolean>();

  selectedFile = signal<File | null>(null);
  isDragging = signal(false);
  errorMessage = signal<string | null>(null);
  filePreviewUrl = signal<string | null>(null);

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled()) {
      this.isDragging.set(true);
      this.dragStateChange.emit(true);
    }
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    this.dragStateChange.emit(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    this.dragStateChange.emit(false);
    this.errorMessage.set(null);

    if (this.disabled()) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event) {
    if (this.disabled()) return;

    this.errorMessage.set(null);
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      this.handleFile(fileList[0]);
    }
  }

  private handleFile(file: File) {
    this.errorMessage.set(null);

    if (this.validateFile(file)) {
      this.selectedFile.set(file);
      this.createFilePreviewUrl(file);

      this.fileSelect.emit(file);
      this.fileChange.emit(this.selectedFile());
    } else {
      const fileInput = document.querySelector(
        'input[type="file"]',
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  }

  private createFilePreviewUrl(file: File): void {
    const blob = new Blob([file], { type: file.type });
    const url = URL.createObjectURL(blob);
    this.filePreviewUrl.set(url);
  }

  isImageFile(): boolean {
    const file = this.selectedFile();
    if (!file) return false;
    return file.type.startsWith('image/');
  }

  isPdfFile(): boolean {
    const file = this.selectedFile();
    if (!file) return false;
    return file.type === 'application/pdf';
  }

  private validateFile(file: File): boolean {
    if (
      this.maxFileSizeMB() > 0 &&
      file.size > this.maxFileSizeMB() * 1024 * 1024
    ) {
      const maxSizeMB = this.maxFileSizeMB();
      const actualSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      this.errorMessage.set(
        `File too large! Maximum size is ${maxSizeMB}MB, but your file is ${actualSizeMB}MB`,
      );
      return false;
    }

    const acceptedTypes = this.acceptedFileTypes();
    if (acceptedTypes.length > 0) {
      const fileType = file.type;
      const isAccepted = acceptedTypes.includes(fileType);

      if (!isAccepted) {
        const supportedTypes = acceptedTypes
          .map((type) => {
            if (type === 'image/jpeg' || type === 'image/jpg') return 'JPG';
            if (type === 'image/png') return 'PNG';
            if (type === 'application/pdf') return 'PDF';
            return type;
          })
          .join(', ');

        this.errorMessage.set(
          `Unsupported file type! Supported formats: ${supportedTypes}`,
        );
        return false;
      }
    }

    return true;
  }

  removeFile() {
    if (this.filePreviewUrl()) {
      URL.revokeObjectURL(this.filePreviewUrl()!);
      this.filePreviewUrl.set(null);
    }

    const hadFile = !!this.selectedFile();
    this.selectedFile.set(null);
    this.errorMessage.set(null);

    if (hadFile) {
      this.fileRemove.emit();
    }
    this.fileChange.emit(null);
  }

  ngOnDestroy(): void {
    if (this.filePreviewUrl()) {
      URL.revokeObjectURL(this.filePreviewUrl()!);
    }
  }

  clearError(): void {
    this.errorMessage.set(null);
  }

  reset(): void {
    this.removeFile();
    this.errorMessage.set(null);
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (!+bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }
}
